import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CurrencyEur, ArrowsClockwise, ArrowRight } from "@phosphor-icons/react";

const SYMBOLS = { EUR: "\u20ac", GBP: "\u00a3", USD: "$" };

export default function Currency() {
  const [base, setBase] = useState("EUR");
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currencies, setCurrencies] = useState([]);

  // Converter
  const [amount, setAmount] = useState("100");
  const [from, setFrom] = useState("GBP");
  const [to, setTo] = useState("EUR");
  const [result, setResult] = useState(null);
  const [converting, setConverting] = useState(false);

  const loadRates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/fx/latest?base=${base}`);
      setRates(r.data);
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    api.get("/fx/currencies").then((r) => setCurrencies(r.data.currencies)).catch(() => {});
  }, []);
  useEffect(() => { loadRates(); }, [loadRates]);

  const doConvert = async () => {
    setConverting(true);
    try {
      const r = await api.get(`/fx/convert?amount=${encodeURIComponent(amount || 0)}&from=${from}&to=${to}`);
      setResult(r.data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Conversion failed" });
    } finally {
      setConverting(false);
    }
  };

  const otherRates = rates ? Object.entries(rates.rates).filter(([c]) => c !== base) : [];

  return (
    <div className="space-y-10" data-testid="currency-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Multi-currency</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Currency Exchange</h1>
          <p className="text-white/60 mt-2 text-sm max-w-2xl">
            Live daily reference rates from the European Central Bank (via Frankfurter). Rates publish
            around 16:00 CET on working days — weekends/holidays use the most recent published rate.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="glass rounded-full p-1 flex gap-1" data-testid="fx-base-switch">
            {["EUR", "GBP", "USD"].map((c) => (
              <button
                key={c}
                onClick={() => setBase(c)}
                data-testid={`fx-base-${c}`}
                className={`px-3 py-1.5 rounded-full text-xs transition ${base === c ? "bg-[#FFB020] text-[#050B0A] font-semibold" : "text-white/60 hover:text-white"}`}
              >
                {c}
              </button>
            ))}
          </div>
          <button onClick={loadRates} disabled={loading} className="btn-ghost text-xs" data-testid="fx-refresh">
            <ArrowsClockwise size={13} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Rates */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow">1 {base} buys</div>
          {rates && (
            <div className="text-xs text-white/45" data-testid="fx-effective-date">
              ECB rate date: {rates.effective_date}
            </div>
          )}
        </div>
        {loading ? (
          <div className="glass rounded-xl p-12 text-center text-white/60 text-sm">Loading rates…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {otherRates.map(([code, val]) => (
              <div key={code} className="glass rounded-xl p-6 flex items-center justify-between" data-testid={`fx-rate-${code}`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center border border-white/10 bg-black/30 font-display text-lg">
                    {SYMBOLS[code] || code}
                  </div>
                  <div>
                    <div className="font-display text-base">{code}</div>
                    <div className="text-xs text-white/45">{(currencies.find((c) => c.code === code) || {}).name || code}</div>
                  </div>
                </div>
                <div className="mono text-2xl" style={{ color: "#FFB020" }}>{val.toFixed(4)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Converter */}
      <div className="glass rounded-xl p-6" data-testid="fx-converter">
        <div className="eyebrow mb-4">Converter</div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto] gap-3 items-end">
          <div>
            <label className="text-xs text-white/50">Amount</label>
            <div className="flex items-center gap-2 mt-1">
              <select value={from} onChange={(e) => setFrom(e.target.value)} className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-sm" data-testid="fx-from">
                {["EUR", "GBP", "USD"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm mono"
                data-testid="fx-amount"
              />
            </div>
          </div>
          <ArrowRight size={18} className="hidden sm:block mb-3 text-white/40" />
          <div>
            <label className="text-xs text-white/50">To</label>
            <select value={to} onChange={(e) => setTo(e.target.value)} className="w-full mt-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="fx-to">
              {["EUR", "GBP", "USD"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button onClick={doConvert} disabled={converting} className="btn-gold justify-center" data-testid="fx-convert-btn">
            {converting ? "…" : "Convert"}
          </button>
        </div>
        {result && (
          <div className="mt-5 pt-5 border-t border-white/10" data-testid="fx-result">
            {result.error ? (
              <div className="text-[#E63946] text-sm">{result.error}</div>
            ) : (
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="mono text-3xl" style={{ color: "#FFB020" }}>
                  {SYMBOLS[result.to]}{result.converted.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
                <span className="text-sm text-white/55">
                  {SYMBOLS[result.from]}{result.amount} · rate {result.rate} · ECB {result.effective_date}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
